import React from "react";
import Dialog from "components/dialog";
import { theme } from "styles/theme";
import Button from "components/form/button";
import { format } from "lib/globalize-util";
import { $, merge } from "glamor";
import { FormLabel } from "@material-ui/core";
import IconButton from "@material-ui/core/IconButton";
import HintBadge from "components/hint-badge";
import { makeStyles } from "@material-ui/styles";
import { ShipDatePicker } from "./ship-date-picker";
import { Form } from "formsy-react";
import CloseIcon from "@material-ui/icons/Close";
import type { IScope } from "reducers/scope";
import moment from "moment";
import AddIcon from "@material-ui/icons/Add";
import MuiButton from "@material-ui/core/Button";
import { IOrder, IShipDate } from "records/order";
import fetch from "lib/fetch";
import ShoppingCartIcon from "@material-ui/icons/ShoppingCart";
import { DATATEST } from "constants/integration";
import { checkDuplicateDate, SNACK_ID } from "./build-order-utils";
import _ from "lodash";
import { stringify } from "query-string";
import DeleteDateWarning from "./delete-warning";
import { BuildOrderActions } from "constants/analytics";
import { IItemRecord } from "records/item/item";
import { ActionProgress } from "./action-progress";
import { WORKING_ORDER_REFRESH_MESSAGE } from "constants/shipments";

const DATES_LIMIT = 30;

const getDefaultShipDate = (window: number) => {
    return {
        ship_start: moment.utc().format("YYYY-MM-DD"),
        ship_end: moment
            .utc()
            .add(window ?? 30, "days")
            .format("YYYY-MM-DD"),
        line_items: [],
    };
};

interface IPendingAdd {
    item: IItemRecord;
    params: object;
}

interface IBuildOrderModalProps {
    open: boolean;
    onRequestClose: () => void;
    scope: IScope;
    router?: any;
    onSyncFromCart: (order: IOrder, meta: { syncToCart?: boolean }) => void;
    onSnackbarAdd: (message: object) => void;
    onCartFetch?: () => void;
    onSnackbarRemove?: (id: string) => void;
    order: IOrder;
    orderId: string;
    isWorkingOrder: boolean;
    legacyApp: any;
    pendingAdds?: IPendingAdd[];
    onBulkAdd: (params) => void;
    onCartAdd: ({ item, params, shipDates }) => void;
}

export const BuildOrderModal: React.FunctionComponent<IBuildOrderModalProps> = ({
    open,
    onRequestClose,
    scope,
    onSyncFromCart,
    onSnackbarAdd,
    onCartFetch,
    onSnackbarRemove,
    order,
    orderId,
    isWorkingOrder = false,
    legacyApp,
    pendingAdds,
    onBulkAdd,
    onCartAdd,
}) => {
    const classes = useStyles();
    const styles = React.useMemo(() => {
        return {
            base: merge(
                $(" .hintIcon", {
                    background: `radial-gradient(circle, ${theme.palette.darkGrey} 0%, rgba(255,255,255,1) 67%)`,
                    borderRadius: 20,
                }),
                $(" .buildOrderLabel", {
                    fontSize: "12px",
                    fontWeight: "bold",
                }),
                $(" .shipDatesList", {
                    display: "flex",
                    flexDirection: "column",
                }),
                $(" .shipDatesWrapper", {
                    display: "flex",
                    flexFlow: "column nowrap",
                    maxHeight: "280px",
                    overflowY: "auto",
                }),
                $(" .shipDatesDescription", {
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    "& span": {
                        fontSize: "1.2rem",
                        fontWeight: "bold",
                    },
                }),
                $(" .shipDatesWindow", {
                    display: "flex",
                    alignItems: "center",
                }),
                $(" .nullDates", {
                    display: "flex",
                    width: "68%",
                    height: "88px",
                    justifyContent: "center",
                    alignItems: "center",
                    background: theme.palette.lightestGrey,
                }),
                $(" .shipDatesDescription", {
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    "& span": {
                        fontSize: "1.2rem",
                        fontWeight: "bold",
                    },
                }),
                $(" .shipDatesWindow", {
                    display: "flex",
                    alignItems: "center",
                }),
                $(" .nullDates", {
                    display: "flex",
                    width: "68%",
                    height: "88px",
                    justifyContent: "center",
                    alignItems: "center",
                    background: theme.palette.lightestGrey,
                }),
                $(" .addShipDateButton", {
                    textTransform: "none",
                    marginTop: "30px",
                    fontSize: "14px",
                    color: theme.palette.darkGrey,
                }),
            ),
            actions: merge(
                {
                    position: "relative",
                },
                $(" .save", {
                    marginLeft: "10px !important",
                }),
                $(" .options", {
                    float: "left !important",
                }),
            ),
            titleIcon: merge({
                fontSize: "1.8rem !important",
                marginRight: "8px",
            }),
        };
    }, []);

    const propShipDates = React.useMemo(() => order?.__build_order?.ship_dates ?? [], [order]);
    const [brandName, brandWindow] = React.useMemo(
        () => [scope?.brand?.name, scope?.brand?.getPreference("default_delivery_window")],
        [scope],
    );
    const [shipDates, setShipDates] = React.useState<IShipDate[]>(
        propShipDates.length ? propShipDates : [getDefaultShipDate(brandWindow)],
    );
    const [dateToRemove, setDateToRemove] = React.useState<number>(-1);
    const [isSaving, setIsSaving] = React.useState<boolean>(false);
    const shipDatesRef = React.useRef<IShipDate[]>(shipDates);
    shipDatesRef.current = shipDates;
    const [newDate, setNewDate] = React.useState<string | null>(null);
    const newDateRef = React.useRef<HTMLDivElement>(null);
    const activeDuplicates = React.useMemo(
        () => !!shipDates.filter((shipDate, idx) => checkDuplicateDate(shipDates, idx)).length,
        [shipDates],
    );
    const invalidDates = React.useMemo(
        () => !!shipDates.filter((shipDate) => !moment(shipDate.ship_start).isValid()).length,
        [shipDates],
    );

    React.useEffect(() => {
        setShipDates(propShipDates.length ? propShipDates : [getDefaultShipDate(brandWindow)]);
        shipDatesRef.current = propShipDates;
    }, [brandWindow, propShipDates]);

    const handleDateChanged = React.useCallback(
        (index, date) => {
            const newShipEnd = moment(date).add(brandWindow, "days");
            const newShipDates = [...shipDatesRef.current];
            newShipDates[index] = {
                ...newShipDates[index],
                ship_start: date,
                ship_end: newShipEnd.format("YYYY-MM-DD"),
            };
            setShipDates(newShipDates);
        },
        [brandWindow],
    );

    const handleDateAdded = React.useCallback(() => {
        let newShipStart = moment.utc();
        const latestDate = [...shipDates]
            .sort((a, b) => moment(a.ship_start).valueOf() - moment(b.ship_start).valueOf())
            .pop();
        if (latestDate) {
            newShipStart = moment(latestDate.ship_end);
            newShipStart.add(1, "days");
        }
        const newShipEnd = newShipStart.clone().add(brandWindow, "days");
        const ship_start = newShipStart.format("YYYY-MM-DD");
        const ship_end = newShipEnd.format("YYYY-MM-DD");
        const newShipDates = [...shipDates, { ship_start, ship_end, line_items: [] }];
        setNewDate(ship_start);
        setShipDates(newShipDates);
    }, [shipDates, brandWindow]);

    React.useEffect(() => {
        const timeout = !newDate
            ? null
            : window.setTimeout(() => {
                  const container = newDateRef.current;
                  if (container) {
                      container.scrollIntoView({ behavior: "smooth" });
                      setNewDate(null);
                  }
              }, 10);

        return () => {
            if (timeout !== null) {
                window.clearTimeout(timeout);
            }
        };
    }, [newDate]);

    const handleDateRemoved = React.useCallback(
        (idx) => {
            const newDates = [...shipDates];
            newDates.splice(idx, 1);
            setShipDates(newDates);
        },
        [shipDates],
    );

    const handleTriggerDateDelete = React.useCallback(
        (idx: number) => () => {
            if (shipDates[idx]?.line_items?.length) {
                setDateToRemove(idx);
                return;
            } else {
                handleDateRemoved(idx);
            }
        },
        [shipDates, handleDateRemoved],
    );

    const handleCloseDeleteWarning = React.useCallback(() => setDateToRemove(-1), []);

    const handleCloseSnack = React.useCallback(() => onSnackbarRemove(SNACK_ID), [onSnackbarRemove]);

    const handleUpdateCart = React.useCallback(
        (newDates: IShipDate[] = undefined, fromDelete: boolean = false) =>
            () => {
                const datesToProcess = newDates ?? shipDates;
                const filteredDates = datesToProcess.filter(
                    (shipDate, idx) => !checkDuplicateDate(datesToProcess, idx),
                );

                const sortedDates = [...filteredDates].sort(
                    (a, b) => moment(a.ship_start).valueOf() - moment(b.ship_start).valueOf(),
                );

                if (filteredDates?.length) {
                    global.analytics.track(BuildOrderActions.isActive);
                }

                setIsSaving(true);
                return fetch(`/working/update/${orderId}`, {
                    method: "POST",
                    body: JSON.stringify({
                        __build_order: {
                            ship_dates: sortedDates,
                        },
                    }),
                })
                    .then((response) => {
                        if (response.ok) {
                            return response.json();
                        } else {
                            throw new Error(format("Problem starting order. Please refresh and try again."));
                        }
                    })
                    .then((data) => {
                        if (pendingAdds.length) {
                            pendingAdds.forEach((pendingAdd) => {
                                const { item, params } = pendingAdd;
                                if (item) {
                                    onCartAdd({ item, params, shipDates: sortedDates });
                                } else {
                                    onBulkAdd(params);
                                }
                            });
                        }
                        onSyncFromCart(data, { syncToCart: true });
                        onCartFetch?.();

                        if (isWorkingOrder) {
                            legacyApp.sendMessage({
                                action: WORKING_ORDER_REFRESH_MESSAGE,
                                data: {
                                    line_items: data.line_items,
                                },
                            });
                        }
                        let snackbarMsg;
                        if (fromDelete) {
                            const dateRemoved = moment(shipDates[dateToRemove]?.ship_start).format("MM/DD/YYYY");
                            handleCloseDeleteWarning();
                            snackbarMsg = `${format("Shipment date removed")} (${dateRemoved})`;
                        } else {
                            onRequestClose();
                            if (
                                filteredDates.length &&
                                !_.isEqual(
                                    propShipDates.map((shipDate) => moment(shipDate.ship_start).format("YYYY-MM-DD")),
                                    filteredDates.map((shipDate) => moment(shipDate.ship_start).format("YYYY-MM-DD")),
                                )
                            ) {
                                snackbarMsg = `${format("Shipment date(s) updated")}`;
                            }
                        }
                        if (snackbarMsg) {
                            onSnackbarAdd({
                                id: SNACK_ID,
                                message: snackbarMsg,
                                shouldUseLatestSnack: true,
                                action: (
                                    <React.Fragment>
                                        <IconButton size="small" color="inherit" onClick={handleCloseSnack}>
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    </React.Fragment>
                                ),
                                messageStyle: {
                                    fontSize: 14,
                                    fontFamily: theme.fontFamily,
                                },
                            });
                        }
                    })
                    .catch((error: Error) => {
                        onSnackbarAdd({ message: error.message });
                    })
                    .finally(() => {
                        setIsSaving(false);
                    });
            },
        [
            onSyncFromCart,
            onRequestClose,
            orderId,
            shipDates,
            onCartFetch,
            propShipDates,
            dateToRemove,
            isWorkingOrder,
            legacyApp,
            pendingAdds,
            onBulkAdd,
            onCartAdd,
            handleCloseDeleteWarning,
            onSnackbarAdd,
            handleCloseSnack,
        ],
    );

    const handleDeleteLineItems = React.useCallback(() => {
        const lineItems = shipDates[dateToRemove]?.line_items ?? [];
        const query = {
            "line-item": lineItems,
        };
        setIsSaving(true);
        fetch(`/api/v2.0/orders/working/line-items?${stringify(query)}`, {
            method: "DELETE",
        })
            .then((response) => {
                if (response.ok) {
                    return response.json();
                } else {
                    throw new Error(format("Problem starting order. Please refresh and try again."));
                }
            })
            .then(() => {
                const newDates = [...shipDates];
                newDates.splice(dateToRemove, 1);
                return handleUpdateCart(newDates, true)();
            })
            .catch((error: Error) => {
                onSnackbarAdd({ message: error.message });
            })
            .finally(() => {
                setIsSaving(false);
            });
    }, [shipDates, dateToRemove, handleUpdateCart, onSnackbarAdd]);

    return (
        <Dialog
            title={
                <div>
                    <ShoppingCartIcon className={`${styles.titleIcon}`} />
                    {format("Order Setup")}
                </div>
            }
            modal={false}
            repositionOnUpdate={false}
            autoScrollBodyContent={false}
            open={open}
            onRequestClose={onRequestClose}
            contentClassName={`${styles.base}`}
            actionsContainerClassName={`${styles.actions}`}
            actions={[
                <ActionProgress open={isSaving} key="ActionProgress" message={format("Updating shipment dates")} />,
                <Button
                    data-test={DATATEST.ORDERS.BUILD_ORDER.CANCEL_BUTTON}
                    label={format("Cancel")}
                    key="cancel"
                    primary={false}
                    onClick={onRequestClose}
                />,
                <Button
                    data-test={DATATEST.ORDERS.BUILD_ORDER.SAVE_BUTTON}
                    className="save"
                    label={format("Save")}
                    key="save"
                    primary={true}
                    disabled={activeDuplicates || invalidDates || isSaving}
                    onClick={handleUpdateCart()}
                />,
            ]}
        >
            <Form className="shipDatesList">
                <div className="shipDatesDescription">
                    <FormLabel className="buildOrderLabel">{format("Choose your shipment dates")}</FormLabel>
                    <div className="shipDatesWindow">
                        <span>
                            {format("|brand| has |window| day ship window", { brand: brandName, window: brandWindow })}
                        </span>
                        <HintBadge
                            message={
                                "|brand| has set |window| day ship windows. They have |window| days to fulfill your order from your initial shipment date."
                            }
                            variables={{ brand: brandName, window: brandWindow }}
                            classes={{ icon: classes.hintIcon }}
                        />
                    </div>
                </div>
                {shipDates.length ? (
                    <div className="shipDatesWrapper">
                        {shipDates.map((shipDate, index) => {
                            return (
                                <ShipDatePicker
                                    key={index + "-" + shipDates.length}
                                    index={index}
                                    shipDate={shipDate}
                                    onChange={handleDateChanged}
                                    onRemove={handleTriggerDateDelete(index)}
                                    isDuplicate={checkDuplicateDate(shipDates, index)}
                                    ref={newDate === shipDate.ship_start ? newDateRef : null}
                                    disabled={isSaving}
                                />
                            );
                        })}
                    </div>
                ) : (
                    <div className="nullDates">{format("No ship dates present.")}</div>
                )}
            </Form>
            <MuiButton
                data-test={DATATEST.ORDERS.BUILD_ORDER.ADD_SHIP_DATE_BUTTON}
                onClick={handleDateAdded}
                className="addShipDateButton"
                size="large"
                startIcon={<AddIcon style={{ color: theme.palette.accent1Color }} />}
                disabled={shipDates.length >= DATES_LIMIT || isSaving}
            >
                {format("Add ship date")}
            </MuiButton>
            <DeleteDateWarning
                open={dateToRemove > -1}
                onConfirm={handleDeleteLineItems}
                onCancel={handleCloseDeleteWarning}
                onRequestClose={handleCloseDeleteWarning}
                shipDate={shipDates[dateToRemove]}
            />
        </Dialog>
    );
};

const useStyles = makeStyles({
    hintIcon: {
        background: `radial-gradient(circle, ${theme.palette.darkGrey} 0%, rgba(255,255,255,1) 67%)`,
        borderRadius: 20,
    },
});
